import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Pagination,
  Typography,
  Tag,
  Tooltip,
  Popover,
  Popconfirm,
  Select as AntSelect,
  DatePicker,
  Button,
  Input,
  Modal,
  Empty,
} from 'antd';
import { Activity, Layers, Boxes, Clock, Filter as FilterIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { formatDateTime } from '@/utils/datetime';
import { toast } from 'react-toastify';

import Icons from '@/components/third-party/Icons';
import TableContainer from '@/components/TableContainer';
import StatCard from '@/components/StatCard';
import ReusableSearchInput from '@/components/ReusableSearch';
import DAGFlow from '@/pages/dashboard/ArchiMateFlow';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { accessControlUtils } from '@/components/AccessControlUtils';
import {
  get_running_batch_details,
  bp_get_recipe_meta_data,
  terminate_batch_details,
} from '@/utils/api';
import {
  EXECUTION_STATUS_LEGEND,
  EXECUTION_STATUS_STYLES,
  resolveOperationExecutionStatus,
} from '@/utils/operationExecutionStatus';

const { RangePicker } = DatePicker;

const { Title } = Typography;

/**
 * Standalone BMR Tracking page (route: /bmr-tracking).
 *
 * Mirrors the AreaConfiguration layout pattern:
 *   <Card title=<icon + title> extra=<legend + search>>
 *     <Row of StatCards>
 *     <Table of running batches>
 *   </Card>
 *
 * Lives independently from ScheduledBatches.js — owns its own state, fetch, and columns.
 */
const BMRTracking = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const selectedModule = useSelector((s) => s.moduleView);
  const location = useLocation();
  const textColor = isDark ? '#ffffff' : '#1f2937';

  // ?batch= query param (set when opened from a ticket's deviation description).
  const batchParam = useMemo(() => {
    try {
      return (new URLSearchParams(location.search).get('batch') || '').trim();
    } catch {
      return '';
    }
  }, [location.search]);

  const [runningList, setRunningList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // Seed the search box from the batch param so the linked batch is pre-filtered.
  const [search, setSearch] = useState(batchParam);

  // DAG view state — selected batch row (when set, renders DAG below the table)
  const [dagBatch, setDagBatch] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Filter state
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [rangeValue, setRangeValue] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [terminateRecord, setTerminateRecord] = useState(null);
  const [terminationComment, setTerminationComment] = useState('');
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const canTerminate = accessControlUtils('BMR Tracking')?.delete_access;

  const fetchRunning = async (overrideSearch = search) => {
    setLoading(true);
    try {
      await get_running_batch_details(
        {
          page_number: page,
          module_type: selectedModule || 'prod',
          ...(overrideSearch && { search: overrideSearch }),
          ...(selectedRecipe && { recipe_id: selectedRecipe }),
          ...(fromDate && { start_time: fromDate }),
          ...(toDate && { end_time: toDate }),
        },
        (res) => {
          if (res?.data?.status === 'success') {
            // Response shape mirrors ScheduledBatches.success_bp_get_batch_details:
            //   data: [ { running_list, schedule_list, completed_list, ... } ]
            //   obj_count: <total>
            const payload = Array.isArray(res?.data?.data) ? res.data.data[0] : res?.data?.data;
            setRunningList(payload?.running_list || []);
            setTotal(
              res?.data?.obj_count ?? payload?.total ?? (payload?.running_list?.length || 0)
            );
          } else {
            setRunningList([]);
            setTotal(0);
          }
          setLoading(false);
        }
      );
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    // On mount/param change, fetch with the batch param so the linked batch is
    // surfaced server-side even if it'd otherwise be on a later page.
    fetchRunning(batchParam || search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedModule, refreshTick, batchParam]);

  // When arriving from a ticket's batch link (?batch=<number>), auto-select that
  // batch row and open its DAG once the rows have loaded. Runs once.
  const autoOpenedRef = useRef(false);

  // Load recipe list once for the filter dropdown.
  useEffect(() => {
    bp_get_recipe_meta_data((res) => {
      if (res?.data?.status === 'success') {
        setRecipes(res?.data?.data || []);
      }
    });
  }, []);

  const resetFilters = () => {
    setSelectedRecipe(null);
    setRangeValue(null);
    setFromDate(null);
    setToDate(null);
    setRefreshTick((n) => n + 1);
  };

  const applyFilters = () => {
    setPage(1);
    setRefreshTick((n) => n + 1);
  };

  const filterPopover = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 260 }}>
      <AntSelect
        showSearch
        allowClear
        size="large"
        value={selectedRecipe || null}
        placeholder="Select MBMR"
        optionFilterProp="children"
        filterOption={(input, option) =>
          (option?.children || '').toLowerCase().includes(input.toLowerCase())
        }
        style={{ width: '100%' }}
        onChange={(v) => setSelectedRecipe(v ?? null)}
      >
        {recipes.map((r) => (
          <AntSelect.Option key={r.recipe_md_id} value={r.recipe_md_id}>
            {r.recipe_md_name}
          </AntSelect.Option>
        ))}
      </AntSelect>
      <RangePicker
        format="YYYY-MM-DD"
        size="large"
        value={rangeValue}
        onChange={(val, dateStrings) => {
          setRangeValue(val);
          setFromDate(dateStrings?.[0] || null);
          setToDate(dateStrings?.[1] || null);
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button size="small" onClick={resetFilters}>
          Reset
        </Button>
        <Button size="small" type="primary" onClick={applyFilters}>
          Apply
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = !!(selectedRecipe || fromDate || toDate);

  const openTerminateModal = (record) => {
    setTerminateRecord(record);
    setTerminationComment('');
    setTerminateModalOpen(true);
  };

  const closeTerminateModal = () => {
    if (terminating) return;
    setTerminateModalOpen(false);
    setTerminateRecord(null);
    setTerminationComment('');
  };

  const terminateBatch = async () => {
    const comment = terminationComment.trim();
    if (!terminateRecord?.batch_number || !comment) return;

    setTerminating(true);
    try {
      await terminate_batch_details(
        {
          batch_number: terminateRecord.batch_number,
          comment,
        },
        (res) => {
          if (res?.status === 'success') {
            toast.success(res.message);
            if (dagBatch?.batch_number === terminateRecord.batch_number) {
              setDagBatch(null);
              setSelectedRowKeys([]);
            }
            setTerminateModalOpen(false);
            setTerminateRecord(null);
            setTerminationComment('');
            setRefreshTick((value) => value + 1);
          } else {
            toast.warning(res?.message || 'Unable to terminate batch');
          }
          setTerminating(false);
        }
      );
    } catch (error) {
      console.error('Error terminating batch:', error);
      toast.error('Unable to terminate batch');
      setTerminating(false);
    }
  };

  const stats = useMemo(() => {
    const list = runningList || [];
    return {
      total: total || list.length || 0,
      master: list.filter((b) => b?.batch_type === 'Master').length,
      lot: list.filter((b) => b?.batch_type === 'Lot').length,
      inProgress: list.filter(
        (b) => b?.operation_status === 'Running' || b?.operation_status === 'In Progress'
      ).length,
    };
  }, [runningList, total]);

  const columns = [
    {
      title: 'Batch No',
      dataIndex: 'batch_number',
      key: 'batch_number',
      width: '13%',
      render: (v) => <span style={{ color: '#2563eb', fontWeight: 600 }}>{v || '—'}</span>,
    },
    {
      title: 'MBMR',
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      width: '14%',
      render: (v) => v || '—',
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: '12%',
      render: (v) => v || '—',
    },
    {
      title: 'Batch Type',
      dataIndex: 'batch_type',
      key: 'batch_type',
      width: '9%',
      render: (v) => {
        if (!v) return '—';
        return <Tag color={v === 'Master' ? 'blue' : 'orange'}>{v}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'operation_status',
      key: 'operation_status',
      width: '10%',
      render: (v, record) => {
        if (!v) return '—';
        const resolved = resolveOperationExecutionStatus({ ...record, operation_status: v });
        return (
          <span>
            <Tag color={resolved.style.tagColor}>{resolved.label}</Tag>
            {resolved.sublabel ? (
              <span style={{ display: 'block', fontSize: 10, color: '#2563eb', marginTop: 2 }}>
                {resolved.sublabel}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      title: 'Start',
      dataIndex: 'batch_start_time',
      key: 'batch_start_time',
      width: '11%',
      render: (v) => formatDateTime(v),
    },
    {
      title: 'Scheduled',
      dataIndex: 'batch_schedule_time',
      key: 'batch_schedule_time',
      width: '12%',
      render: (v) => formatDateTime(v),
    },
    {
      title: 'Batch Size',
      dataIndex: 'batch_size',
      key: 'batch_size',
      width: '9%',
      render: (v) => v ?? '—',
    },
    // "View Batch Workflow" moved to /batches → Ongoing BMR tab. This Actions
    // column now only carries Terminate, so it's shown only when the user can
    // terminate (otherwise it would render empty).
    canTerminate && {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Popconfirm
            title="Are you sure you want to terminate this Batch?"
            onConfirm={(event) => {
              event?.stopPropagation?.();
              openTerminateModal(record);
            }}
            onCancel={(event) => event?.stopPropagation?.()}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Tooltip title="Terminate Batch">
              <button
                type="button"
                onClick={(event) => event.stopPropagation()}
                className="p-1 rounded-md bg-red-500 text-white transition-transform duration-200 hover:bg-red-600 hover:scale-110"
              >
                <Icons.StopCircle className="w-5 h-5" />
              </button>
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ].filter(Boolean);

  const filteredRows = useMemo(() => {
    const q = (search || '').toLowerCase();
    return (runningList || [])
      .filter((b) => {
        if (!q) return true;
        return (
          (b?.batch_number || '').toLowerCase().includes(q) ||
          (b?.recipe_name || '').toLowerCase().includes(q) ||
          (b?.product_name || '').toLowerCase().includes(q)
        );
      })
      .map((b, i) => ({ ...b, key: b.id ?? i }));
  }, [runningList, search]);

  // Re-arm auto-open whenever a new batch param arrives.
  useEffect(() => {
    autoOpenedRef.current = false;
  }, [batchParam]);

  // Auto-select the linked batch (?batch=<number>) and open its DAG once rows load.
  useEffect(() => {
    if (!batchParam || autoOpenedRef.current || !filteredRows.length) return;
    const wanted = batchParam.toLowerCase();
    const target =
      filteredRows.find((b) => (b?.batch_number || '').trim().toLowerCase() === wanted) ||
      filteredRows.find((b) => (b?.batch_number || '').trim().toLowerCase().includes(wanted));
    // eslint-disable-next-line no-console
    console.log(
      '[BMRTracking auto-open] param=',
      batchParam,
      'rows=',
      filteredRows.map((b) => b.batch_number),
      'matched=',
      target?.batch_number
    );
    if (target) {
      autoOpenedRef.current = true;
      setSelectedRowKeys([target.key]);
      setDagBatch(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, batchParam]);

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border rounded-lg border-gray-300 shadow-md"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        bodyStyle={{ paddingTop: 12 }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Activity className="text-blue-600" size={24} />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {selectedModule === 'prod' ? t('BMR Tracking') : t('BPR Tracking')}
            </Title>
          </div>
        }
        extra={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 border-r pr-3 border-gray-300 flex-wrap">
              <Tooltip title="Master batch">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-500 inline-block" />
                  <span className="text-sm font-medium" style={{ color: textColor }}>
                    Master
                  </span>
                </div>
              </Tooltip>
              <Tooltip title="Lot batch">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" />
                  <span className="text-sm font-medium" style={{ color: textColor }}>
                    Lot
                  </span>
                </div>
              </Tooltip>
            </div>
            <div
              className="flex items-center gap-2 border-r pr-3 border-gray-300 flex-wrap max-w-[620px]"
              title="Operation execution colors (batch flow)"
            >
              {EXECUTION_STATUS_LEGEND.map((meta) => {
                const cfg = EXECUTION_STATUS_STYLES[meta.key];
                if (!cfg) return null;
                return (
                  <Tooltip key={meta.key} title={meta.description || cfg.label}>
                    <div className="flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                        style={{ backgroundColor: cfg.color }}
                      />
                      <span
                        className="text-xs font-medium whitespace-nowrap"
                        style={{ color: textColor }}
                      >
                        {cfg.label}
                      </span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
            <Popover trigger="click" placement="bottomRight" content={filterPopover}>
              <Tooltip title="Filter">
                <button
                  className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors relative"
                  type="button"
                >
                  <FilterIcon size={18} color={hasActiveFilters ? '#2563eb' : '#475569'} />
                  {hasActiveFilters && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#2563eb',
                      }}
                    />
                  )}
                </button>
              </Tooltip>
            </Popover>
            <ReusableSearchInput
              placeholder="Search batches..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={() => fetchRunning(search)}
            />
          </div>
        }
      >
        {dagBatch ? (
          <div
            style={{
              border: '2px dashed #b3afaf',
              padding: 10,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <Title level={5} style={{ margin: 0, color: textColor }}>
                Batch Flow · {dagBatch?.batch_number || ''}
              </Title>
              <Button
                size="small"
                onClick={() => {
                  setDagBatch(null);
                  setSelectedRowKeys([]);
                }}
              >
                Close DAG
              </Button>
            </div>
            <DAGFlow
              data={dagBatch}
              set_flow_data={(v) => {
                if (v === null) {
                  setDagBatch(null);
                  setSelectedRowKeys([]);
                }
              }}
            />
          </div>
        ) : (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                label="Total Running BMRs"
                value={stats.total}
                icon={<Activity size={24} />}
                bgColor="#dbeafe"
                iconColor="#3b82f6"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                label="Master Batches"
                value={stats.master}
                icon={<Layers size={24} />}
                bgColor="#dbeafe"
                iconColor="#2563eb"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                label="Lot Batches"
                value={stats.lot}
                icon={<Boxes size={24} />}
                bgColor="#ffedd5"
                iconColor="#f97316"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                label="In Progress"
                value={stats.inProgress}
                icon={<Clock size={24} />}
                bgColor="#dcfce7"
                iconColor="#16a34a"
              />
            </Col>
          </Row>
        )}

        {/* Fixed-height, bordered table — same convention as /ticket, /process-library,
            /recipe-approval: tableLayout="fixed" + percentage column widths keep columns
            full-width and stable regardless of row count. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            // overflow: 'visible' (not 'hidden') — 'hidden' clips the table's own
            // horizontal scrollbar at the bottom edge (same bug found on Batch360.js).
            overflow: 'visible',
          }}
        >
          <TableContainer
            bordered
            loading={loading}
            data={filteredRows}
            columns={columns}
            tableLayout="fixed"
            rowSelection={{
              type: 'radio',
              selectedRowKeys,
              hideSelectAll: true,
              onChange: (keys, rows) => {
                const row = rows?.[0] || null;
                setSelectedRowKeys(keys);
                setDagBatch(row);
              },
            }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRowKeys([record.key]);
                setDagBatch(record);
              },
              style: { cursor: 'pointer' },
            })}
            rowClassName={(record) => {
              if (record?.batch_type === 'Master') return 'bg-blue-50';
              if (record?.batch_type === 'Lot') return 'bg-orange-50';
              return '';
            }}
            // Responsive: viewport-anchored so it scales on larger/smaller screens
            height="calc(100vh - 404px)"
            locale={{ emptyText: <Empty description="No running BMRs found" /> }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 12,
          }}
        >
          <Pagination
            total={total}
            current={page}
            pageSize={10}
            showSizeChanger={false}
            showTotal={(t) => `Total ${t} BMR's`}
            onChange={(value) => setPage(value)}
          />
        </div>

        <Modal
          title={`Terminate Batch${
            terminateRecord?.batch_number ? ` · ${terminateRecord.batch_number}` : ''
          }`}
          open={terminateModalOpen}
          onOk={terminateBatch}
          onCancel={closeTerminateModal}
          okText="Submit"
          cancelText="Cancel"
          confirmLoading={terminating}
          okButtonProps={{ disabled: !terminationComment.trim() }}
          destroyOnClose
        >
          <label htmlFor="termination-comment" style={{ display: 'block', marginBottom: 8 }}>
            Comment
          </label>
          <Input.TextArea
            id="termination-comment"
            rows={4}
            placeholder="Enter termination reason..."
            value={terminationComment}
            onChange={(event) => setTerminationComment(event.target.value)}
            disabled={terminating}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default BMRTracking;
